/* Inklok — Sprint 2 UI
 *
 * Twee tabbladen:
 *  1. **Ik ga inklokken** — medewerker gebruikt browser geolocation → inklok/pauze/uitklok.
 *     Server bepaalt via Haversine of het binnen de geofence valt; anders `pending_approval`.
 *  2. **Goedkeuringswachtrij** — zichtbaar voor directie/superadmin/locatiemanager: lijst met
 *     alle `pending_approval` events, kan goedkeuren/afwijzen met opmerking.
 *
 * NB: bewust GEEN foto-capture — user-preference "zonder inklok foto's".
 */
import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, MapPin, LogIn, LogOut, Coffee, CheckCircle2, AlertCircle,
  ClipboardList, Loader2, Check, X, Download, TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const EVENT_LABELS = {
  clock_in: { label: "Inklokken", icon: LogIn, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  clock_out: { label: "Uitklokken", icon: LogOut, color: "bg-neutral-100 text-neutral-800 border-neutral-200" },
  break_start: { label: "Pauze start", icon: Coffee, color: "bg-amber-100 text-amber-800 border-amber-200" },
  break_end: { label: "Pauze eind", icon: Coffee, color: "bg-amber-50 text-amber-700 border-amber-100" },
};

const STATUS_LABELS = {
  ok: { label: "Goedgekeurd", color: "bg-emerald-100 text-emerald-800" },
  approved: { label: "Goedgekeurd (review)", color: "bg-emerald-100 text-emerald-800" },
  pending_approval: { label: "Wacht op goedkeuring", color: "bg-amber-100 text-amber-800" },
  rejected: { label: "Afgewezen", color: "bg-red-100 text-red-800" },
};

const REVIEWER_ROLES = ["superadmin", "platform_admin", "directeur", "locatiemanager"];
const EXPORT_ROLES = ["superadmin", "platform_admin", "directeur", "financieel"];

export default function InklokPage() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canReview = REVIEWER_ROLES.includes(role);
  const canExport = EXPORT_ROLES.includes(role);
  const [tab, setTab] = useState(canReview ? "wachtrij" : "inklokken");

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Inkloksysteem"
        subtitle="Registreer je werktijd met GPS-controle. Buiten de geofence? Dan gaat het naar de goedkeuringswachtrij."
      />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inklokken" data-testid="inklok-tab-medewerker">
            <Clock className="h-4 w-4 mr-1.5" /> Mijn kloktijden
          </TabsTrigger>
          {canReview && (
            <TabsTrigger value="wachtrij" data-testid="inklok-tab-wachtrij">
              <ClipboardList className="h-4 w-4 mr-1.5" /> Goedkeuringswachtrij
            </TabsTrigger>
          )}
          {canReview && (
            <TabsTrigger value="samenvatting" data-testid="inklok-tab-summary">
              <TrendingUp className="h-4 w-4 mr-1.5" /> Wekelijkse samenvatting
            </TabsTrigger>
          )}
          {canExport && (
            <TabsTrigger value="export" data-testid="inklok-tab-export">
              <Download className="h-4 w-4 mr-1.5" /> Salarisexport
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="inklokken" className="mt-4"><MedewerkerTab /></TabsContent>
        {canReview && <TabsContent value="wachtrij" className="mt-4"><WachtrijTab /></TabsContent>}
        {canReview && <TabsContent value="samenvatting" className="mt-4"><SamenvattingTab /></TabsContent>}
        {canExport && <TabsContent value="export" className="mt-4"><ExportTab /></TabsContent>}
      </Tabs>
    </div>
  );
}

function MedewerkerTab() {
  const [eligibility, setEligibility] = useState({ locations: [], groups: [], hard_max_radius_m: 50, max_gps_age_s: 30 });
  const [locId, setLocId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [events, setEvents] = useState([]);
  const [gpsPos, setGpsPos] = useState(null);  // {lat,lng,accuracy_m,gps_timestamp,age_s}
  const [checkResult, setCheckResult] = useState(null);  // {inside,distance_m,...}
  const [gpsErr, setGpsErr] = useState("");
  const [busy, setBusy] = useState(false);

  const groupsForLoc = eligibility.groups.filter(g => g.location_id === locId);

  const load = async () => {
    try {
      const r = await api.get("/inklok/eligibility");
      setEligibility(r.data);
      if (r.data.locations?.length === 1 && !locId) {
        setLocId(r.data.locations[0].id);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon toegewezen locaties niet laden");
    }
    try {
      const r = await api.get("/inklok/mijn-events?limit=15");
      setEvents(r.data.items || []);
    } catch (e) { /* stil */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Auto-select single group als medewerker maar aan één groep gekoppeld is voor deze locatie
  useEffect(() => {
    if (locId) {
      // reset group + gps als locatie wijzigt
      setGroupId("");
      setCheckResult(null);
      const gs = eligibility.groups.filter(g => g.location_id === locId);
      if (gs.length === 1) setGroupId(gs[0].id);
    }
  }, [locId]); // eslint-disable-line

  useEffect(() => { setCheckResult(null); }, [groupId]);

  const grabGps = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("GPS niet beschikbaar in deze browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy || 0,
        gps_timestamp: new Date(pos.timestamp || Date.now()).toISOString(),
      }),
      err => {
        const msg = err.code === 1 ? "GPS-toestemming geweigerd — sta locatie toe in je browser."
                  : err.code === 2 ? "GPS-positie niet beschikbaar — ga naar buiten of controleer je verbinding."
                  : err.code === 3 ? "GPS-verzoek verlopen — probeer opnieuw."
                  : (err.message || "GPS-fout");
        reject(new Error(msg));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

  const doGeoCheck = async () => {
    if (!locId || !groupId) return;
    setBusy(true); setGpsErr(""); setCheckResult(null);
    try {
      const gps = await grabGps();
      setGpsPos(gps);
      const r = await api.post("/inklok/geocheck", {
        location_id: locId, lat: gps.lat, lng: gps.lng,
        accuracy_m: gps.accuracy_m, gps_timestamp: gps.gps_timestamp,
      });
      setCheckResult(r.data);
      if (r.data.inside) {
        toast.success(`Binnen geofence (${r.data.distance_m}m). Je kunt nu inklokken.`);
      } else {
        toast.warning((r.data.afwijkingen || []).join(" · "));
      }
    } catch (e) {
      const msg = e.message || formatApiError(e.response?.data?.detail) || "GPS-check mislukt";
      setGpsErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const registerEvent = async (event_type) => {
    if (!locId) { toast.error("Kies eerst een locatie"); return; }
    if (!groupId) { toast.error("Kies eerst een groep"); return; }
    setBusy(true); setGpsErr("");
    try {
      // Verse GPS-fix ophalen — server weigert fixes > 30s oud
      const gps = await grabGps();
      setGpsPos(gps);
      const r = await api.post("/inklok/event", {
        location_id: locId, group_id: groupId, event_type,
        lat: gps.lat, lng: gps.lng, accuracy_m: gps.accuracy_m,
        gps_timestamp: gps.gps_timestamp,
        device_info: navigator.userAgent?.slice(0, 100),
      });
      const st = r.data?.status;
      if (st === "ok") {
        toast.success(`${EVENT_LABELS[event_type].label} geregistreerd — binnen geofence`);
      } else {
        toast.warning(`${EVENT_LABELS[event_type].label} verstuurd — wacht op goedkeuring: ${(r.data?.afwijkingen || []).join(", ")}`);
      }
      // Reset check zodat volgende actie opnieuw expliciet moet
      setCheckResult(null);
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message || "Onbekende fout";
      setGpsErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const stepDone = { loc: !!locId, group: !!groupId, gps: !!(checkResult && checkResult.inside) };
  const canClock = stepDone.loc && stepDone.group && stepDone.gps;

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-4" data-testid="inklok-medewerker-card">
        {/* Stap-indicator */}
        <div className="flex items-center gap-2 text-xs" data-testid="inklok-steps">
          <StepPill n={1} label="Locatie" done={stepDone.loc} />
          <span className="text-neutral-300">→</span>
          <StepPill n={2} label="Groep" done={stepDone.group} disabled={!stepDone.loc} />
          <span className="text-neutral-300">→</span>
          <StepPill n={3} label="GPS-check" done={stepDone.gps} disabled={!stepDone.group} />
          <span className="text-neutral-300">→</span>
          <StepPill n={4} label="Inklokken" done={false} disabled={!canClock} />
        </div>

        {/* Stap 1: Locatie */}
        <div className="flex items-start gap-3">
          <MapPin className="h-5 w-5 text-sage-700 mt-1" />
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-widest text-neutral-500">1. Kies locatie</Label>
            {eligibility.locations.length === 0 ? (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-1" data-testid="inklok-geen-locaties">
                Je bent nog niet gekoppeld aan een locatie met actief inkloksysteem.
                Vraag directie om je toe te wijzen (via <code>/app/beveiliging</code>).
              </div>
            ) : (
              <Select value={locId} onValueChange={setLocId}>
                <SelectTrigger className="mt-1" data-testid="inklok-locatie-select">
                  <SelectValue placeholder="Kies locatie" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {eligibility.locations.map(l => (
                    <SelectItem key={l.id} value={l.id} data-testid={`inklok-locatie-opt-${l.id}`}>
                      {l.name} <span className="text-neutral-400 text-xs">· max {l.radius_m}m</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        {/* Stap 2: Groep — pas zichtbaar wanneer locatie gekozen */}
        {locId && (
          <div className="flex items-start gap-3 pl-8">
            <div className="flex-1">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">2. Kies groep</Label>
              {groupsForLoc.length === 0 ? (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-1" data-testid="inklok-geen-groepen">
                  Geen groepen gekoppeld voor deze locatie.
                </div>
              ) : (
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="mt-1" data-testid="inklok-groep-select">
                    <SelectValue placeholder="Kies groep" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {groupsForLoc.map(g => (
                      <SelectItem key={g.id} value={g.id} data-testid={`inklok-groep-opt-${g.id}`}>
                        {g.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}

        {/* Stap 3: GPS-check — pas zichtbaar wanneer groep gekozen */}
        {locId && groupId && (
          <div className="flex items-start gap-3 pl-8">
            <div className="flex-1 space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-500">3. Controleer je locatie</Label>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={doGeoCheck} disabled={busy} data-testid="inklok-btn-geocheck">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />}
                  GPS-check
                </Button>
                {checkResult && (
                  checkResult.inside ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="inklok-check-ok">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Binnen geofence ({checkResult.distance_m}m)
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 border border-red-200" data-testid="inklok-check-fail">
                      <AlertCircle className="h-3 w-3 mr-1" /> Buiten geofence ({checkResult.distance_m}m)
                    </Badge>
                  )
                )}
              </div>
              {gpsPos && (
                <div className="text-xs text-neutral-500">
                  GPS: {gpsPos.lat.toFixed(5)}, {gpsPos.lng.toFixed(5)} (±{Math.round(gpsPos.accuracy_m)}m)
                </div>
              )}
              {checkResult && !checkResult.inside && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="inklok-check-afwijkingen">
                  {(checkResult.afwijkingen || []).join(" · ")}
                </div>
              )}
              {gpsErr && !checkResult && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                  {gpsErr}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stap 4: Inklok-knoppen — pas ontgrendeld na geslaagde GPS-check */}
        {locId && groupId && (
          <div className="pl-8">
            <Label className="text-xs uppercase tracking-widest text-neutral-500">4. Registreer je actie</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {["clock_in", "break_start", "break_end", "clock_out"].map(et => {
                const meta = EVENT_LABELS[et];
                const Icon = meta.icon;
                const primary = et === "clock_in" || et === "clock_out";
                return (
                  <Button
                    key={et}
                    onClick={() => registerEvent(et)}
                    disabled={busy || !canClock}
                    variant={primary ? "default" : "outline"}
                    className={primary ? "text-white" : ""}
                    style={primary && canClock ? { background: "var(--sage)" } : {}}
                    data-testid={`inklok-btn-${et}`}
                  >
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}
                    {meta.label}
                  </Button>
                );
              })}
            </div>
            {!canClock && (
              <p className="text-xs text-neutral-500 mt-2" data-testid="inklok-unlock-hint">
                Voer eerst de GPS-check uit om de knoppen te ontgrendelen.
              </p>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5" data-testid="inklok-mijn-events">
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4" /> Recente kloktijden
        </h3>
        {events.length === 0 ? (
          <div className="text-sm text-neutral-500 py-6 text-center">Nog geen kloktijden geregistreerd</div>
        ) : (
          <div className="space-y-1.5">
            {events.map(ev => {
              const meta = EVENT_LABELS[ev.event_type] || { label: ev.event_type, icon: Clock, color: "bg-neutral-100" };
              const stMeta = STATUS_LABELS[ev.status] || { label: ev.status, color: "bg-neutral-100" };
              return (
                <div key={ev.id} className="flex items-center justify-between text-sm border-b border-neutral-100 pb-1.5">
                  <div className="flex items-center gap-2">
                    <Badge className={`${meta.color} border`} variant="outline">{meta.label}</Badge>
                    <span className="text-neutral-600">
                      {ev.location_name}
                      {ev.group_name && <span className="text-neutral-400"> · {ev.group_name}</span>}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-neutral-500">
                      {new Date(ev.server_time).toLocaleString("nl-NL")}
                    </span>
                    <Badge className={stMeta.color} variant="outline">{stMeta.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function StepPill({ n, label, done, disabled }) {
  const cls = done
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : disabled
    ? "bg-neutral-100 text-neutral-400 border-neutral-200"
    : "bg-sage-50 text-sage-800 border-sage-200";
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 ${cls}`}
      data-testid={`inklok-step-${n}`}
    >
      <span className="font-mono text-[10px]">{done ? "✓" : n}</span>
      <span>{label}</span>
    </span>
  );
}

function WachtrijTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);  // {event, decision}
  const [opmerking, setOpmerking] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/inklok/approval-queue");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon wachtrij niet laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!review) return;
    if (review.decision === "rejected" && !opmerking.trim()) {
      toast.error("Bij afwijzen is een opmerking verplicht"); return;
    }
    setBusy(true);
    try {
      await api.post(`/inklok/${review.event.id}/review`, {
        beslissing: review.decision,
        opmerking: opmerking.trim() || null,
      });
      toast.success(review.decision === "approved" ? "Goedgekeurd" : "Afgewezen");
      setReview(null); setOpmerking(""); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Review mislukt");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-8 text-neutral-500">Wachtrij laden…</div>;

  return (
    <Card className="p-5" data-testid="inklok-wachtrij-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium flex items-center gap-2">
          <ClipboardList className="h-4 w-4" /> Wacht op goedkeuring
        </h3>
        <Badge variant="outline" data-testid="inklok-wachtrij-count">{items.length}</Badge>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
          <div className="text-neutral-500">Geen openstaande reviews</div>
        </div>
      ) : (
        <div className="divide-y divide-neutral-100">
          {items.map(ev => {
            const meta = EVENT_LABELS[ev.event_type] || { label: ev.event_type, icon: Clock, color: "" };
            return (
              <div key={ev.id} className="py-3" data-testid={`inklok-wachtrij-row-${ev.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      <Badge className={`${meta.color} border`} variant="outline">{meta.label}</Badge>
                      {ev.user_name}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {ev.location_name} · {new Date(ev.server_time).toLocaleString("nl-NL")}
                    </div>
                    <div className="text-xs mt-1 flex items-start gap-1.5">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5" />
                      <span className="text-amber-800">{(ev.afwijkingen || []).join(" · ") || "onbekend"}</span>
                    </div>
                    {ev.distance_from_center_m != null && (
                      <div className="text-xs text-neutral-500 mt-0.5">
                        Afstand tot locatiecentrum: {ev.distance_from_center_m}m · GPS ±{Math.round(ev.accuracy_m || 0)}m
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline"
                            onClick={() => { setReview({ event: ev, decision: "approved" }); setOpmerking(""); }}
                            data-testid={`inklok-approve-${ev.id}`}>
                      <Check className="h-4 w-4 mr-1" /> Goedkeuren
                    </Button>
                    <Button size="sm" variant="outline"
                            className="text-red-700 border-red-200 hover:bg-red-50"
                            onClick={() => { setReview({ event: ev, decision: "rejected" }); setOpmerking(""); }}
                            data-testid={`inklok-reject-${ev.id}`}>
                      <X className="h-4 w-4 mr-1" /> Afwijzen
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!review} onOpenChange={o => !o && setReview(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>
              {review?.decision === "approved" ? "Kloktijd goedkeuren" : "Kloktijd afwijzen"}
            </DialogTitle>
          </DialogHeader>
          {review && (
            <div className="space-y-3">
              <div className="text-sm text-neutral-600">
                <div>{EVENT_LABELS[review.event.event_type]?.label} — {review.event.user_name}</div>
                <div className="text-xs text-neutral-500">
                  {review.event.location_name} · {new Date(review.event.server_time).toLocaleString("nl-NL")}
                </div>
              </div>
              <div>
                <Label className="text-xs">
                  Opmerking {review.decision === "rejected" && <span className="text-red-500">*</span>}
                </Label>
                <Input value={opmerking} onChange={e => setOpmerking(e.target.value)}
                       placeholder={review.decision === "approved" ? "Optioneel" : "Reden van afwijzing (verplicht)"}
                       data-testid="inklok-review-opmerking" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setReview(null)}>Annuleren</Button>
                <Button onClick={submit} disabled={busy}
                        className={review.decision === "approved" ? "text-white" : ""}
                        style={review.decision === "approved" ? { background: "var(--sage)" } : {}}
                        variant={review.decision === "approved" ? "default" : "destructive"}
                        data-testid="inklok-review-confirm">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {review.decision === "approved" ? "Goedkeuren" : "Afwijzen"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Wekelijkse samenvatting — per medewerker + tiny sparkline
// ---------------------------------------------------------------------------

function Sparkline({ values, width = 90, height = 24 }) {
  if (!values || values.length === 0) {
    return <div className="h-6 w-24 text-neutral-300 text-xs">—</div>;
  }
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - (v / max) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg width={width} height={height} className="text-sage-700">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: "var(--sage)" }} />
      {values.map((v, i) => (
        <circle key={i} cx={i * step} cy={height - (v / max) * height} r="1.5" fill="currentColor" style={{ color: "var(--sage)" }} />
      ))}
    </svg>
  );
}

function SamenvattingTab() {
  const [data, setData] = useState({ weeks: [], rows: [] });
  const [weeks, setWeeks] = useState(8);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/inklok/summary?weeks=${weeks}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kon samenvatting niet laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [weeks]); // eslint-disable-line

  return (
    <Card className="p-5" data-testid="inklok-samenvatting-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Uren per medewerker
        </h3>
        <Select value={String(weeks)} onValueChange={v => setWeeks(parseInt(v))}>
          <SelectTrigger className="w-40" data-testid="inklok-samenvatting-periode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="4">Laatste 4 weken</SelectItem>
            <SelectItem value="8">Laatste 8 weken</SelectItem>
            <SelectItem value="13">Laatste 13 weken</SelectItem>
            <SelectItem value="26">Laatste 26 weken</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[0,1,2].map(i => <div key={i} className="h-10 bg-neutral-100 animate-pulse rounded" />)}
        </div>
      ) : data.rows.length === 0 ? (
        <div className="text-center py-8 text-neutral-500">Geen goedgekeurde uren in deze periode</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide border-b">
                <th className="py-2 pr-3">Medewerker</th>
                <th className="py-2 pr-3">Actieve weken</th>
                <th className="py-2 pr-3">Totaal (u)</th>
                <th className="py-2 pr-3">Gem. per week</th>
                <th className="py-2 pr-3">Trend</th>
                <th className="py-2 pr-3">Verloop</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {data.rows.map(r => {
                const TrendIcon = r.trend === "up" ? TrendingUp : r.trend === "down" ? TrendingDown : Minus;
                const trendColor = r.trend === "up" ? "text-emerald-600"
                                  : r.trend === "down" ? "text-red-600"
                                  : "text-neutral-400";
                return (
                  <tr key={r.user_id} data-testid={`inklok-samenvatting-row-${r.user_id}`}>
                    <td className="py-2 pr-3 font-medium">{r.user_name}</td>
                    <td className="py-2 pr-3 text-neutral-600">{r.active_weeks}</td>
                    <td className="py-2 pr-3 font-mono">{r.total.toFixed(2)}</td>
                    <td className="py-2 pr-3 font-mono text-neutral-600">{r.avg_per_active_week.toFixed(2)}</td>
                    <td className="py-2 pr-3">
                      <TrendIcon className={`h-4 w-4 ${trendColor}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <Sparkline values={r.weekly} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-neutral-500 mt-3">
        Alleen kloktijden met status "ok" of "goedgekeurd via review" tellen mee. Trend vergelijkt de laatste 2 weken met de 2 weken daarvoor.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Salarisexport — CSV/XLSX per periode
// ---------------------------------------------------------------------------

function ExportTab() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + "01";
  const [from, setFrom] = useState(firstOfMonth);
  const [to, setTo] = useState(today);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/inklok/salaris-export?date_from=${from}&date_to=${to}&format=json`);
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Preview mislukt");
    } finally { setBusy(false); }
  };

  const downloadExport = async (format) => {
    setBusy(true);
    try {
      const r = await api.get(`/inklok/salaris-export?date_from=${from}&date_to=${to}&format=${format}`, {
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salaris_${from}_${to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} gedownload`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Download mislukt");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="inklok-export-card">
      <div className="flex items-center gap-2">
        <Download className="h-4 w-4" />
        <h3 className="font-medium">Salarisexport per periode</h3>
      </div>
      <p className="text-sm text-neutral-600">
        Genereer een CSV- of Excel-export van alle goedgekeurde kloktijden binnen de gekozen periode.
        Uren worden gekoppeld tot shifts (clock_in → clock_out) met pauze-aftrek.
      </p>
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <div>
          <Label className="text-xs">Van</Label>
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)}
                 data-testid="inklok-export-from" />
        </div>
        <div>
          <Label className="text-xs">Tot</Label>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)}
                 data-testid="inklok-export-to" />
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" onClick={loadPreview} disabled={busy}
                data-testid="inklok-export-preview">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardList className="h-4 w-4 mr-2" />}
          Preview
        </Button>
        <Button onClick={() => downloadExport("csv")} disabled={busy}
                className="text-white" style={{ background: "var(--sage)" }}
                data-testid="inklok-export-csv">
          <Download className="h-4 w-4 mr-2" /> Download CSV
        </Button>
        <Button onClick={() => downloadExport("xlsx")} disabled={busy}
                className="text-white" style={{ background: "var(--sage)" }}
                data-testid="inklok-export-xlsx">
          <Download className="h-4 w-4 mr-2" /> Download Excel
        </Button>
      </div>

      {preview && (
        <div className="border rounded-md p-4 bg-neutral-50" data-testid="inklok-export-preview-result">
          <div className="text-sm font-medium mb-2">
            {preview.shifts?.length || 0} shifts gevonden — {preview.totalen?.length || 0} medewerker(s)
          </div>
          <table className="w-full text-xs">
            <thead className="text-neutral-500">
              <tr>
                <th className="text-left py-1">Medewerker</th>
                <th className="text-right py-1">Shifts</th>
                <th className="text-right py-1">Netto uren</th>
                <th className="text-right py-1">Openstaand</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {(preview.totalen || []).map(t => (
                <tr key={t.user_id}>
                  <td className="py-1">{t.user_name}</td>
                  <td className="py-1 text-right font-mono">{t.shifts}</td>
                  <td className="py-1 text-right font-mono">{t.netto_uren.toFixed(2)}</td>
                  <td className="py-1 text-right font-mono">
                    {t.unclosed > 0 ? (
                      <span className="text-amber-600">{t.unclosed}</span>
                    ) : <span className="text-neutral-400">0</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

